import { useState, useEffect } from 'react';
import { optionalProp } from '../../core';
import { MLPVersion } from '../../types/enums';
import Pagination from '../Pagination/Pagination';
import type {
  DynamicObjectInterface,
  PaginationDataInterface,
  SortingDataInterface,
  TableProps,
} from '../types/propType';
import { TableBody, TableHeader } from '../TableComponents';
import { handleSorting } from '../tableUtils';
import styles from './DefaultTable.module.scss';

const DefaultTable = ({
  columns,
  dataSource,
  rowKey,
  onRowClick,
  selected,
  setSelectedKeys,
  selectedKeys = [],
  className,
  pagination = false,
  sortingCallback,
  paginationObject,
  paginationCallback,
  enableCheckbox = false,
  enableRadioButton = false,
  formatPaginationCallback,
  disabledItems,
  disabledCheckboxes,
  disabledRadioButtons,
  forwardedRef,
  mlpVersion = MLPVersion.TWO,
  tableVersion,
  hasNextPage,
  pageSizeData,
  defaultCheckedAllRows,
  scrollToTopRefId,
  adminSortingCallback,
  message,
  columnsWithOverflowVisible = [],
  shouldFocusRow,
  ...rest
}: TableProps) => {
  const initialPagination = paginationObject || {
    totalRecords: dataSource.length,
    rowsPerPage: 5,
    currentPage: 0,
    recordStartIndex: 0,
    recordEndIndex: 5,
  };
  const [paginationData, setPaginationData] = useState<PaginationDataInterface>(initialPagination);
  const [sortingData, setSortingData] = useState<SortingDataInterface>({});
  const [tableData, setTableData] = useState<DynamicObjectInterface[]>(dataSource);
  const [selectedItemKeys, setSelectedItemKeys] = useState<string[]>(selectedKeys);

  useEffect(() => {
    if (sortingCallback || adminSortingCallback) {
      setTableData(dataSource); // assuming the callback function directly updated the dataSource list without returning sorted data
    } else {
      const sortedData = handleSorting(sortingData, dataSource);
      setTableData(sortedData);
    }
  }, [dataSource]);

  const getWidth = (width = 0) => {
    if (width) {
      return { width: `${width}%` };
    }

    return {};
  };

  return (
    <>
      <table
        className={`${styles['table-container']} ${className ? className : ''}`}
        ref={forwardedRef}
        {...rest}
        data-testid="table-data-test-id"
      >
        <TableHeader
          columns={columns}
          tableData={tableData}
          getWidth={getWidth}
          sortingData={sortingData}
          setSortingData={setSortingData}
          setTableData={setTableData}
          pagination={pagination}
          paginationData={paginationData}
          styles={styles}
          enableCheckbox={enableCheckbox}
          enableRadioButton={enableRadioButton}
          isServerSideFetch={!!paginationCallback}
          setSelectedKeys={(keys) => {
            setSelectedItemKeys(keys);
            if (setSelectedKeys) {
              setSelectedKeys(keys);
            }
          }}
          selectedKeys={selectedItemKeys}
          {...optionalProp('defaultCheckedAllRows', defaultCheckedAllRows)}
          {...optionalProp('disabledCheckboxes', disabledCheckboxes)}
          {...optionalProp('rowKey', rowKey)}
          {...optionalProp('sortingCallback', sortingCallback)}
          {...optionalProp('adminSortingCallback', adminSortingCallback)}
          {...optionalProp('disabledItems', disabledItems)}
        />
        {tableData.length === 0 && message && (
          <tr>
            <td colSpan={columns.length + 1}>
              <div className={styles['message']} data-testid="table-msg-test-id">
                {message}
              </div>
            </td>
          </tr>
        )}
        <TableBody
          columns={columns}
          tableData={tableData}
          setTableData={setTableData}
          setSelectedKeys={(keys) => {
            setSelectedItemKeys(keys);
            if (setSelectedKeys) {
              setSelectedKeys(keys);
            }
          }}
          selectedKeys={selectedItemKeys}
          rowKey={rowKey as string}
          pagination={pagination}
          paginationData={paginationData}
          getWidth={getWidth}
          styles={styles}
          isServerSideFetch={!!paginationCallback}
          columnsWithOverflowVisible={columnsWithOverflowVisible}
          tableVersion={tableVersion}
          enableCheckbox={enableCheckbox}
          enableRadioButton={enableRadioButton}
          {...optionalProp('selected', selected)}
          {...optionalProp('onRowClick', onRowClick)}
          {...optionalProp('disabledItems', disabledItems)}
          {...optionalProp('disabledCheckboxes', disabledCheckboxes)}
          {...optionalProp('disabledRadioButtons', disabledRadioButtons)}
          {...optionalProp('shouldFocusRow', shouldFocusRow)}
        />
      </table>
      {pagination && tableData.length > 0 ? (
        <Pagination
          mlpVersion={mlpVersion}
          {...(paginationObject || paginationData)}
          updatePaginationData={(updatedData) => {
            paginationCallback?.({
              ...paginationData,
              ...updatedData,
            });
            setPaginationData({
              ...paginationData,
              ...updatedData,
            });
          }}
          {...optionalProp('formatPaginationCallback', formatPaginationCallback)}
          {...optionalProp('hasNextPage', hasNextPage)}
          {...optionalProp('pageSizeData', pageSizeData)}
          {...optionalProp('scrollToTopRefId', scrollToTopRefId)}
        />
      ) : (
        ''
      )}
    </>
  );
};
DefaultTable.displayName = 'DefaultTable';

export default DefaultTable;
